import {
  AllSalesRepayment,
  OperationsRepaymentPage,
  RepaymentDailyAggregate,
  RepaymentsService,
} from '@/service/repayments.service'
import { Repayment } from '@/domain/repayments'
import { CommonModule } from '@angular/common'
import { Component, HostBinding, OnDestroy, OnInit } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'
import { ButtonModule } from 'primeng/button'
import { InputTextModule } from 'primeng/inputtext'
import { SkeletonModule } from 'primeng/skeleton'
import { TableModule } from 'primeng/table'
import { TabsModule } from 'primeng/tabs'
import { SelectModule } from 'primeng/select'
import { ToggleSwitchModule } from 'primeng/toggleswitch'
import { SelectButtonModule } from 'primeng/selectbutton'
import { AllSalesTableComponent } from '../all-sales-table/all-sales-table.component'
import {
  RepaymentReportTableComponent,
  RepaymentReportTableGroup,
  RepaymentReportTableRow,
  RepaymentReportCellSelection,
} from '../repayment-report-table/repayment-report-table.component'
import {
  OperationsRepaymentTableComponent,
  OperationsRepaymentTableLayout,
} from '../gocardless-repayment-table/gocardless-repayment-table.component'
import {
  LegalInternalRecoveriesRow,
  LegalInternalRecoverySelection,
  LegalInternalRecoveriesTableComponent,
} from '../legal-internal-recoveries-table/legal-internal-recoveries-table.component'
import {
  LegalRepaymentsChartBar,
  LegalRepaymentsChartComponent,
} from '../legal-repayments-chart/legal-repayments-chart.component'
import {
  AllSalesCellSelection,
  AllSalesColumn,
  AllSalesMerchantTable,
  DEFAULT_ALL_SALES_TABLE_LAYOUT,
  SalesMetrics,
} from '../all-sales-table/all-sales-table.model'

type ReportMetric = 'sum' | 'count'
type ReportDateMode = 'repayment' | 'created'
type DailyRepaymentView = 'weekly' | 'this-month' | 'monthly' | 'gocardless' | 'processor-fees' | 'legal-fees-recovered'
type OperationsRecordView = Extract<DailyRepaymentView, 'gocardless' | 'processor-fees' | 'legal-fees-recovered'>
type LegalRecordView = 'write-off-since-inception'
type RecordRepaymentView = OperationsRecordView | LegalRecordView
type LegalView = 'internal-recoveries' | 'total-legal-repayments' | LegalRecordView
type ReportTab = 'legal' | 'daily-repayments' | 'all-sales'

const REPORT_DATA_SOURCES = [
  'APS',
  'Cardnet',
  'Cardone',
  'FDMS',
  'Gocardless',
  'Dojo (Direct Split)',
  'Modulr',
  'TrueLayer',
] as const

const REPORT_DATA_SOURCE_NAMES: Record<string, (typeof REPORT_DATA_SOURCES)[number]> = {
  aps: 'APS',
  cardnet: 'Cardnet',
  cardone: 'Cardone',
  fdms: 'FDMS',
  gocardless: 'Gocardless',
  'dojo (direct split)': 'Dojo (Direct Split)',
  modulr: 'Modulr',
  truelayer: 'TrueLayer',
}

const REPORT_DATA_SOURCE_VALUES: Record<
  (typeof REPORT_DATA_SOURCES)[number],
  string[]
> = {
  APS: ['APS'],
  Cardnet: ['Cardnet'],
  Cardone: ['CardOne', 'Cardone'],
  FDMS: ['FDMS'],
  Gocardless: ['Gocardless', 'GoCardless'],
  'Dojo (Direct Split)': ['Dojo (Direct Split)'],
  Modulr: ['Modulr'],
  TrueLayer: ['TrueLayer'],
}

const UNSPECIFIED_DATA_SOURCE = 'Unspecified'
const LEGAL_INTERNAL_RECOVERY_SOURCES = ['TrueLayer', 'Legal', 'Gocardless', 'Direct Payment'] as const
const LEGAL_INTERNAL_RECOVERY_SOURCE_VALUES = [
  'TrueLayer', 'Legal', 'Gocardless', 'GoCardless', 'Direct Payment',
]

const OPERATIONS_RECORD_LAYOUTS: Record<RecordRepaymentView, OperationsRepaymentTableLayout> = {
  gocardless: {
    ariaLabel: 'All GoCardless payments',
    emptyMessage: 'No GoCardless repayments were found.',
    columns: [
      { label: 'Repayment: Created Date', value: 'created-date' },
      { label: 'Repayment Date', value: 'repayment-date' },
      { label: 'Payment Amount', value: 'payment-amount' },
      { label: 'Repayment: Repayment Name', value: 'repayment-name' },
      { label: 'Data Source', value: 'data-source' },
      { label: 'Cash Advance', value: 'cash-advance' },
      { label: 'Repayment: Created By', value: 'created-by' },
      { label: 'Terminal', value: 'terminal' },
    ],
  },
  'processor-fees': {
    ariaLabel: 'Check Processor fees',
    emptyMessage: 'No Processor Fees repayments were found.',
    columns: [
      { label: 'Date', value: 'repayment-date' },
      { label: 'Merchant: Customer Number (*)', value: 'customer-number' },
      { label: 'Merchant: Customer Status ↓', value: 'customer-status' },
      { label: 'Data Source', value: 'data-source' },
      { label: 'Payment Amount', value: 'payment-amount' },
    ],
  },
  'legal-fees-recovered': {
    ariaLabel: 'Legal Fees Recovered payments',
    emptyMessage: 'No Legal Fees Recovered repayments were found.',
    columns: [
      { label: 'Date ↑', value: 'repayment-date' },
      { label: 'Repayment: Repayment Name', value: 'repayment-name' },
      { label: 'Cash Advance', value: 'cash-advance' },
      { label: 'Payment Amount', value: 'payment-amount' },
    ],
  },
  'write-off-since-inception': {
    ariaLabel: 'Write Off Since Inception repayments',
    emptyMessage: 'No Write Off repayments were found.',
    columns: [
      { label: 'Repayment Date', value: 'repayment-date' },
      { label: 'Customer Number', value: 'customer-number' },
      { label: 'Cash Advance', value: 'cash-advance' },
      { label: 'Merchant', value: 'customer-name' },
      { label: 'Description', value: 'description' },
      { label: 'Payment Amount', value: 'payment-amount' },
      { label: 'Data Source', value: 'data-source' },
      { label: 'Comments', value: 'comments' },
    ],
  },
}

const OPERATIONS_RECORD_DATA_SOURCES: Record<RecordRepaymentView, string[]> = {
  gocardless: ['Gocardless', 'GoCardless'],
  'processor-fees': ['Processor Fees'],
  'legal-fees-recovered': ['Legal Fees Recovered'],
  'write-off-since-inception': ['Write Off'],
}

interface ReportRow {
  repaymentDate: string
  values: Record<string, { sum: number; count: number }>
}

interface ReportCellSelection {
  date: string
  source: (typeof REPORT_DATA_SOURCES)[number]
}

interface LegalMerchantPaymentRow {
  merchantName: string
  repaymentDate: string
  createdDate: string
  customerNumber: string
  paymentAmount: number
}

@Component({
  selector: 'app-reports',
  standalone: true,
  templateUrl: './reports.component.html',
  styleUrl: './reports.component.scss',
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    SkeletonModule,
    TableModule,
    TabsModule,
    SelectModule,
    ToggleSwitchModule,
    SelectButtonModule,
    AllSalesTableComponent,
    RepaymentReportTableComponent,
    OperationsRepaymentTableComponent,
    LegalInternalRecoveriesTableComponent,
    LegalRepaymentsChartComponent,
  ],
})
export class ReportsComponent implements OnInit, OnDestroy {
  private static readonly allSalesMerchantStorageKey = 'reports.allSales.merchantIds'
  private static readonly allSalesTerminalOnlyStorageKey = 'reports.allSales.terminalOnly'
  readonly reportTabs: Array<{ label: string; value: ReportTab }> = [
    { label: 'Legal', value: 'legal' },
    { label: 'Operations', value: 'daily-repayments' },
    { label: 'Merchant Report', value: 'all-sales' },
  ]
  activeReportTab: ReportTab = this.reportTabs[0].value
  embedded = false

  @HostBinding('class.embedded-report-host')
  get embeddedReportHost(): boolean {
    return this.embedded
  }
  readonly metricOptions: { label: string; value: ReportMetric }[] = [
    { label: 'Sum', value: 'sum' },
    { label: 'Count', value: 'count' },
  ]
  metric: ReportMetric = 'sum'
  readonly dailyRepaymentViewOptions: { label: string; value: DailyRepaymentView }[] = [
    { label: 'Recent', value: 'weekly' },
    { label: 'This month', value: 'this-month' },
    { label: 'Monthly', value: 'monthly' },
    { label: 'Gocardless', value: 'gocardless' },
    { label: 'Processor Fees', value: 'processor-fees' },
    { label: 'Legal Fees Recovered', value: 'legal-fees-recovered' },
  ]
  dailyRepaymentView: DailyRepaymentView = 'weekly'
  monthlyAllSources = false
  /** Months before today shown by the This month report; zero is the current month. */
  createdMonthOffset = 0
  dateMode: ReportDateMode = 'repayment'
  daysBack = 7
  loading = true
  error: string | null = null
  dataSources: string[] = [...REPORT_DATA_SOURCES]
  rows: ReportRow[] = []
  groupedReportRows: RepaymentReportTableGroup[] = []
  operationsRepayments: Repayment[] = []
  operationsTotalRecords = 0
  operationsTotalPaymentAmount = 0
  operationsLoadingMore = false
  operationsHasMore = false
  private operationsSummaryLoaded = false
  readonly legalViewOptions: Array<{ label: string; value: LegalView }> = [
    { label: 'Total Legal Repayments', value: 'total-legal-repayments' },
    { label: 'Internal Recoveries', value: 'internal-recoveries' },
    { label: 'Write Off Since Inception', value: 'write-off-since-inception' },
  ]
  readonly legalInternalRecoverySources = [...LEGAL_INTERNAL_RECOVERY_SOURCES]
  legalView: LegalView = this.legalViewOptions[0].value
  legalLoading = false
  legalError: string | null = null
  legalRows: LegalInternalRecoveriesRow[] = []
  legalChartBars: LegalRepaymentsChartBar[] = []
  legalSelectedCell: LegalInternalRecoverySelection | null = null
  legalDetailRows: LegalMerchantPaymentRow[] = []
  legalDetailLoading = false
  legalDetailError: string | null = null
  selectedCell: ReportCellSelection | null = null
  selectedGroupedReportCell: RepaymentReportCellSelection | null = null
  selectedAllSalesCell: AllSalesCellSelection | null = null
  detailRepayments: Repayment[] = []
  detailLoading = false
  detailError: string | null = null
  merchantOptions: Array<{ label: string; value: string }> = []
  selectedMerchantId: string | null = null
  private selectedMerchantLabel = ''
  merchantOptionsLoading = false
  terminalOnly = false
  allSalesLoading = false
  allSalesError: string | null = null
  allSalesMonths: string[] = []
  private allSalesRepayments: AllSalesRepayment[] = []
  allSalesMerchantTables: AllSalesMerchantTable[] = []
  readonly allSalesViewportLayout = {
    ...DEFAULT_ALL_SALES_TABLE_LAYOUT,
    height: '100%',
  }

  private readonly lifecycleAbort = new AbortController()
  private destroyed = false
  private loadSequence = 0
  private legalLoadSequence = 0
  private legalDetailLoadSequence = 0
  private readonly operationsPageSize = 200
  private detailLoadSequence = 0
  private merchantSearchSequence = 0
  private merchantSearchTimeout: ReturnType<typeof setTimeout> | null = null
  private readonly naturalNameCollator = new Intl.Collator(undefined, {
    numeric: true,
    sensitivity: 'base',
  })

  constructor(
    private readonly repaymentsService: RepaymentsService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
  ) {}

  get tableRows(): Array<ReportRow | number> {
    return this.loading ? this.skeletonRows : this.rows
  }

  get detailTableRows(): any[] {
    return this.detailLoading ? [1, 2, 3, 4, 5] : this.detailRepayments
  }

  get legalDetailTableRows(): any[] {
    return this.legalDetailLoading ? [1, 2, 3, 4] : this.legalDetailRows
  }

  get skeletonRows(): number[] {
    return Array.from({ length: Math.min(this.daysBack, 10) }, (_, index) => index + 1)
  }

  get isEmbeddedAllSales(): boolean {
    return this.embedded && this.activeReportTab === 'all-sales'
  }

  ngOnInit(): void {
    const embeddedReportTab = this.route.snapshot.data['embeddedReportTab'] as
      | ReportTab
      | undefined
    this.embedded = this.route.snapshot.data['embedded'] === true && Boolean(embeddedReportTab)
    if (this.embedded && embeddedReportTab) {
      this.activeReportTab = embeddedReportTab
      document.body.classList.add('embedded')
      // Canvas reports are intentionally always light. The host can preserve
      // a user's normal application preference without changing this iframe.
      document.documentElement.classList.remove('p-dark')
    }
    const requestedTab = this.route.snapshot.queryParamMap.get('reportTab')
    const hasValidRequestedTab = this.reportTabs.some((tab) => tab.value === requestedTab)
    if (!this.embedded && hasValidRequestedTab) {
      this.activeReportTab = requestedTab as ReportTab
    }
    if (this.isEmbeddedAllSales) {
      this.selectedMerchantId = this.embeddedMerchantId()
      this.terminalOnly = false
    } else {
      this.restoreAllSalesFilters()
    }
    this.daysBack = this.normalizeDaysBack(
      this.route.snapshot.queryParamMap.get('reportDays'),
    )
    if (!this.embedded && !this.route.snapshot.queryParamMap.has('reportDays')) {
      void this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { reportDays: this.daysBack, reportTab: this.activeReportTab },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      })
    } else if (!this.embedded && !hasValidRequestedTab) {
      void this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { reportTab: this.activeReportTab },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      })
    }
    if (this.activeReportTab === 'legal') {
      this.loadActiveLegalView()
    } else {
      void this.loadReport()
    }
    if (this.selectedMerchantId) {
      this.addSelectedMerchantOption()
    }
    if (this.activeReportTab === 'all-sales' && this.selectedMerchantId) {
      void this.loadAllSalesReport()
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true
    if (this.merchantSearchTimeout) {
      clearTimeout(this.merchantSearchTimeout)
    }
    this.lifecycleAbort.abort()
  }

  setMetric(metric: ReportMetric): void {
    this.metric = metric
  }

  onDailyRepaymentViewChange(): void {
    void this.loadReport()
  }

  loadMoreOperationsRepayments(): void {
    const recordView = this.activeRecordView
    if (!recordView || this.loading || this.operationsLoadingMore || !this.operationsHasMore) return
    void this.loadOperationsReport(false, this.loadSequence, recordView)
  }

  onMonthlyAllSourcesChange(): void {
    void this.loadReport()
  }

  onLegalViewChange(): void {
    this.clearLegalDetail()
    this.loadActiveLegalView()
  }

  showPreviousCreatedMonth(): void {
    this.createdMonthOffset -= 1
    void this.loadReport()
  }

  showNextCreatedMonth(): void {
    if (this.isCurrentCreatedMonth) return
    this.createdMonthOffset += 1
    void this.loadReport()
  }

  updateDaysBack(value: number | string): void {
    const daysBack = this.normalizeDaysBack(value)
    if (daysBack === this.daysBack) return

    this.daysBack = daysBack
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { reportDays: daysBack },
      queryParamsHandling: 'merge',
    })
    void this.loadReport()
  }

  toggleDateMode(): void {
    this.dateMode = this.dateMode === 'repayment' ? 'created' : 'repayment'
    void this.loadReport()
  }

  get dateFieldLabel(): string {
    return this.dateMode === 'repayment' ? 'Repayment date' : 'Created date'
  }

  get dateModeToggleLabel(): string {
    return this.dateMode === 'repayment' ? 'Created date' : 'Repayment date'
  }

  get isWeeklyRepaymentView(): boolean {
    return this.dailyRepaymentView === 'weekly'
  }

  get isOperationsRecordView(): boolean {
    return this.dailyRepaymentView === 'gocardless'
      || this.dailyRepaymentView === 'processor-fees'
      || this.dailyRepaymentView === 'legal-fees-recovered'
  }

  get isLegalRecordView(): boolean {
    return this.legalView === 'write-off-since-inception'
  }

  get activeRecordView(): RecordRepaymentView | null {
    if (this.activeReportTab === 'legal' && this.isLegalRecordView) {
      return 'write-off-since-inception'
    }
    return this.isOperationsRecordView ? this.dailyRepaymentView as OperationsRecordView : null
  }

  get operationsRecordLayout(): OperationsRepaymentTableLayout {
    return OPERATIONS_RECORD_LAYOUTS[this.dailyRepaymentView as OperationsRecordView]
  }

  get writeOffRecordLayout(): OperationsRepaymentTableLayout {
    return OPERATIONS_RECORD_LAYOUTS['write-off-since-inception']
  }

  get isCurrentCreatedMonth(): boolean {
    return this.createdMonthOffset === 0
  }

  get selectedCreatedMonthLabel(): string {
    return new Intl.DateTimeFormat('en-GB', {
      month: 'long',
      year: 'numeric',
    }).format(this.selectedCreatedMonthDate())
  }

  formatValue(value: number): string {
    if (this.metric === 'count') {
      return new Intl.NumberFormat('en-GB').format(value)
    }

    return this.formatAmount(value)
  }

  formatAmount(value: number | string | undefined): string {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(this.toNumber(value))
  }

  cashAdvanceNumber(repayment: Repayment): string {
    const value = repayment.cash_advances?.name || repayment.cash_advance_id
    return value ? value.replace(/\s*-\d+$/, '') : '—'
  }

  valueFor(row: ReportRow, source: string): number {
    return row.values[source]?.[this.metric] ?? 0
  }

  formatReportValue(row: ReportRow, source: string): string {
    return (row.values[source]?.count ?? 0) === 0 ? '' : this.formatValue(this.valueFor(row, source))
  }

  totalFor(source: string): number {
    return this.rows.reduce(
      (total, row) => total + this.valueFor(row, source),
      0,
    )
  }

  totalCountFor(source: string): number {
    return this.rows.reduce((total, row) => total + (row.values[source]?.count ?? 0), 0)
  }

  exportSelectedRepayments(): void {
    if ((!this.selectedCell && !this.selectedGroupedReportCell && !this.selectedAllSalesCell) || !this.detailRepayments.length) return

    const csv = [
      [
        'repayment_date',
        'created_date',
        'description',
        'amount_gbp',
        'customer_number',
      ],
      ...this.detailRepayments.map((repayment) => [
        repayment.repayment_date ?? '',
        repayment.created_date ?? '',
        repayment.description ?? '',
        String(this.toNumber(repayment.payment_amount)),
        this.cashAdvanceNumber(repayment),
      ]),
    ]
      .map((row) => row.map((value) => this.csvValue(value)).join(','))
      .join('\r\n')

    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    const selectionName = this.selectedCell?.source.toLowerCase()
      ?? this.selectedGroupedReportCell?.source.toLowerCase()
      ?? this.selectedAllSalesCell?.terminalName.toLowerCase().replace(/\s+/g, '-')
      ?? 'all-sales'
    const selectionDate = this.selectedCell?.date ?? this.selectedGroupedReportCell?.createdDate ?? this.selectedGroupedReportCell?.repaymentMonth ?? this.selectedAllSalesCell?.month ?? 'report'
    link.download = `repayments-${selectionName}-${selectionDate}.csv`
    link.click()
    URL.revokeObjectURL(url)
  }

  isSelectedCell(row: ReportRow, source: string): boolean {
    return (
      this.selectedCell?.date === row.repaymentDate &&
      this.selectedCell.source === source
    )
  }

  selectCell(row: ReportRow, source: string): void {
    const canonicalSource = this.canonicalDataSource(source)
    if (!canonicalSource) return

    this.selectedCell = { date: row.repaymentDate, source: canonicalSource }
    void this.loadCellRepayments()
  }

  clearSelection(): void {
    this.selectedCell = null
    this.selectedGroupedReportCell = null
    this.selectedAllSalesCell = null
    this.detailRepayments = []
    this.detailError = null
  }

  formatDate(date: string): string {
    const isoMatch = date.match(/^(\d{4})-(\d{2})-(\d{2})/)
    const ukMatch = date.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
    const [year, month, day] = isoMatch
      ? isoMatch.slice(1).map(Number)
      : ukMatch
        ? [Number(ukMatch[3]), Number(ukMatch[2]), Number(ukMatch[1])]
        : []
    const parsed = new Date(year, month - 1, day)
    if (Number.isNaN(parsed.getTime())) return date

    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(parsed)
  }

  retry(): void {
    if (this.activeReportTab === 'legal') {
      this.loadActiveLegalView()
    } else {
      void this.loadReport()
    }
  }

  onReportTabChange(value: string | number): void {
    if (this.embedded) return
    this.activeReportTab = String(value) as ReportTab
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { reportTab: this.activeReportTab },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    })
    if (this.activeReportTab === 'all-sales' && !this.allSalesMerchantTables.length && !this.allSalesLoading) {
      void this.loadAllSalesReport()
    }
    if (this.activeReportTab === 'daily-repayments') {
      void this.loadReport()
    }
    if (this.activeReportTab === 'legal') {
      if (this.isLegalRecordView && !this.operationsRepayments.length && !this.loading) {
        this.loadActiveLegalView()
      } else if (!this.isLegalRecordView && !this.legalRows.length && !this.legalLoading) {
        this.loadActiveLegalView()
      }
    }
  }

  reloadAllSales(): void {
    if (this.activeReportTab === 'all-sales') {
      void this.loadAllSalesReport()
    }
  }

  onMerchantSelectionChange(): void {
    this.selectedMerchantLabel = this.merchantOptions.find(
      (option) => option.value === this.selectedMerchantId,
    )?.label ?? ''
    this.persistAllSalesFilters()
    this.reloadAllSales()
  }

  onMerchantFilter(event: { filter?: string }): void {
    const term = event.filter ?? ''
    const searchSequence = ++this.merchantSearchSequence
    if (this.merchantSearchTimeout) {
      clearTimeout(this.merchantSearchTimeout)
    }

    if (!term.trim()) {
      this.merchantOptions = []
      this.addSelectedMerchantOption()
      this.merchantOptionsLoading = false
      return
    }

    this.merchantSearchTimeout = setTimeout(() => {
      this.merchantSearchTimeout = null
      void this.searchMerchantOptions(term, searchSequence)
    }, 300)
  }

  onTerminalOnlyChange(): void {
    this.persistAllSalesFilters()
    this.reloadAllSales()
  }

  private async loadReport(): Promise<void> {
    const loadSequence = ++this.loadSequence
    if (this.isOperationsRecordView) {
      await this.loadOperationsReport(true, loadSequence)
      return
    }
    this.loading = true
    this.error = null
    this.rows = []
    this.groupedReportRows = []
    this.resetOperationsReport()
    this.clearSelection()
    const dates = this.reportDateRange()

    try {
      const aggregates = this.isWeeklyRepaymentView
        ? await this.repaymentsService.fetchDailyRepaymentAggregates(
          this.dateField,
          dates[0],
          this.dayAfterToday(),
          { signal: this.lifecycleAbort.signal },
        )
        : this.dailyRepaymentView === 'monthly'
          ? await this.repaymentsService.fetchMonthlyRepaymentAggregates(
            this.monthlyAllSources ? undefined : Object.values(REPORT_DATA_SOURCE_VALUES).flat(),
            { signal: this.lifecycleAbort.signal },
          )
        : await this.repaymentsService.fetchRepaymentReportAggregates(
          this.selectedMonthStart(),
          this.selectedMonthEnd(),
          { signal: this.lifecycleAbort.signal },
        )

      if (this.destroyed || loadSequence !== this.loadSequence) return

      this.dataSources = this.dailyRepaymentView === 'monthly' && this.monthlyAllSources
        ? [...new Set(aggregates.map((aggregate) => aggregate.data_source?.trim() || UNSPECIFIED_DATA_SOURCE))]
          .sort((left, right) => left.localeCompare(right))
        : [...REPORT_DATA_SOURCES]
      if (this.isWeeklyRepaymentView) {
        this.rows = this.buildRows(this.reportDates(dates, aggregates), aggregates)
      } else {
        this.groupedReportRows = this.buildGroupedReportRows(aggregates)
      }
    } catch (error) {
      if (
        this.destroyed ||
        this.lifecycleAbort.signal.aborted ||
        this.isRequestAborted(error) ||
        loadSequence !== this.loadSequence
      ) {
        return
      }
      this.error = error instanceof Error ? error.message : 'Failed to load reports.'
    } finally {
      if (!this.destroyed && loadSequence === this.loadSequence) {
        this.loading = false
      }
    }
  }

  private async loadLegalReport(): Promise<void> {
    const loadSequence = ++this.legalLoadSequence
    this.legalLoading = true
    this.legalError = null
    this.legalRows = []
    this.legalChartBars = []
    this.clearLegalDetail()

    try {
      const aggregates = await this.repaymentsService.fetchMonthlyRepaymentAggregates(
        LEGAL_INTERNAL_RECOVERY_SOURCE_VALUES,
        { signal: this.lifecycleAbort.signal },
      )
      if (this.destroyed || loadSequence !== this.legalLoadSequence) return
      this.legalRows = this.buildLegalInternalRecoveryRows(aggregates)
      this.legalChartBars = this.buildLegalRepaymentChartBars(aggregates)
    } catch (error) {
      if (this.destroyed || this.lifecycleAbort.signal.aborted || this.isRequestAborted(error) || loadSequence !== this.legalLoadSequence) return
      this.legalError = error instanceof Error ? error.message : 'Failed to load internal recoveries.'
    } finally {
      if (!this.destroyed && loadSequence === this.legalLoadSequence) {
        this.legalLoading = false
      }
    }
  }

  private loadActiveLegalView(): void {
    if (this.isLegalRecordView) {
      this.legalLoadSequence += 1
      this.legalLoading = false
      this.legalError = null
      const loadSequence = ++this.loadSequence
      void this.loadOperationsReport(true, loadSequence, 'write-off-since-inception')
      return
    }

    this.loadSequence += 1
    this.loading = false
    this.operationsLoadingMore = false
    void this.loadLegalReport()
  }

  private buildLegalInternalRecoveryRows(
    aggregates: RepaymentDailyAggregate[],
  ): LegalInternalRecoveriesRow[] {
    const rows = new Map<string, LegalInternalRecoveriesRow>()
    aggregates.forEach((aggregate) => {
      const month = this.repaymentMonthKey(aggregate.repayment_month)
      const rawSource = aggregate.data_source?.trim()
      const source = rawSource === 'GoCardless' ? 'Gocardless' : rawSource
      if (!month || !source || !LEGAL_INTERNAL_RECOVERY_SOURCES.some((candidate) => candidate === source)) return

      const row = rows.get(month) ?? { month, values: {}, total: 0 }
      const amount = this.toNumber(aggregate.repayment_sum)
      row.values[source] = (row.values[source] ?? 0) + amount
      row.total += amount
      rows.set(month, row)
    })

    return [...rows.values()].sort((left, right) => right.month.localeCompare(left.month))
  }

  private buildLegalRepaymentChartBars(
    aggregates: RepaymentDailyAggregate[],
  ): LegalRepaymentsChartBar[] {
    const amountsByMonth = new Map<string, { amount: number; count: number }>()
    aggregates.forEach((aggregate) => {
      const month = this.repaymentMonthKey(aggregate.repayment_month)
      if (!month || aggregate.data_source?.trim() !== 'Legal') return
      const current = amountsByMonth.get(month) ?? { amount: 0, count: 0 }
      current.amount += this.toNumber(aggregate.repayment_sum)
      current.count += this.toNumber(aggregate.repayment_count)
      amountsByMonth.set(month, current)
    })
    return [...amountsByMonth.entries()]
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([month, value]) => ({ month, amount: value.amount, count: value.count }))
  }

  onLegalInternalRecoveryCellSelected(selection: LegalInternalRecoverySelection): void {
    this.legalSelectedCell = selection
    void this.loadLegalInternalRecoveryDetail()
  }

  clearLegalDetail(): void {
    this.legalDetailLoadSequence += 1
    this.legalSelectedCell = null
    this.legalDetailRows = []
    this.legalDetailLoading = false
    this.legalDetailError = null
  }

  private async loadLegalInternalRecoveryDetail(): Promise<void> {
    if (!this.legalSelectedCell) return
    const selection = this.legalSelectedCell
    const loadSequence = ++this.legalDetailLoadSequence
    this.legalDetailLoading = true
    this.legalDetailError = null
    this.legalDetailRows = []

    try {
      const contributions = await this.repaymentsService.fetchLegalInternalRecoveryRepayments(
        selection.month,
        this.legalRecoverySourcesFor(selection.source),
        { signal: this.lifecycleAbort.signal },
      )
      if (this.destroyed || loadSequence !== this.legalDetailLoadSequence) return
      this.legalDetailRows = contributions
        .map((contribution) => ({
          merchantName: this.relatedName(contribution.merchants, 'Unassigned merchant'),
          repaymentDate: contribution.repayment_date ?? '',
          createdDate: contribution.created_date ?? '',
          customerNumber: this.relatedName(contribution.cash_advances, '—').replace(/\s*-\d+$/, ''),
          paymentAmount: this.toNumber(contribution.payment_sum),
        }))
        .sort((left, right) => right.repaymentDate.localeCompare(left.repaymentDate)
          || this.naturalNameCollator.compare(left.merchantName, right.merchantName))
    } catch (error) {
      if (this.destroyed || this.isRequestAborted(error) || loadSequence !== this.legalDetailLoadSequence) return
      this.legalDetailError = error instanceof Error ? error.message : 'Failed to load contributing payments.'
    } finally {
      if (!this.destroyed && loadSequence === this.legalDetailLoadSequence) {
        this.legalDetailLoading = false
      }
    }
  }

  private legalRecoverySourcesFor(source?: string): string[] {
    if (!source) return LEGAL_INTERNAL_RECOVERY_SOURCE_VALUES
    return source === 'Gocardless' ? ['Gocardless', 'GoCardless'] : [source]
  }

  formatLegalRecoveryMonth(month: string): string {
    const match = month.match(/^(\d{4})-(\d{2})$/)
    return match
      ? new Intl.DateTimeFormat('en-GB', { month: 'long', year: 'numeric' })
        .format(new Date(Number(match[1]), Number(match[2]) - 1, 1))
      : month
  }

  private async loadOperationsReport(
    reset: boolean,
    expectedLoadSequence = this.loadSequence,
    recordView: RecordRepaymentView | null = this.activeRecordView,
  ): Promise<void> {
    if (!recordView) return
    if (reset) {
      this.loading = true
      this.error = null
      this.resetOperationsReport()
      this.clearSelection()
      void this.loadOperationsSummary(recordView, expectedLoadSequence)
    } else {
      this.operationsLoadingMore = true
    }

    try {
      const page = await this.repaymentsService.fetchOperationsRepayments(
        OPERATIONS_RECORD_DATA_SOURCES[recordView],
        this.operationsRepayments.length,
        this.operationsPageSize,
        {
          signal: this.lifecycleAbort.signal,
          orderByRepaymentDate: recordView === 'write-off-since-inception',
        },
      )
      if (
        this.destroyed
        || expectedLoadSequence !== this.loadSequence
        || this.activeRecordView !== recordView
      ) return

      this.applyOperationsPage(page, reset)
    } catch (error) {
      if (this.destroyed || this.lifecycleAbort.signal.aborted || this.isRequestAborted(error) || expectedLoadSequence !== this.loadSequence) return
      this.error = error instanceof Error ? error.message : 'Failed to load Operations repayments.'
    } finally {
      if (!this.destroyed && expectedLoadSequence === this.loadSequence) {
        this.loading = false
        this.operationsLoadingMore = false
      }
    }
  }

  private applyOperationsPage(page: OperationsRepaymentPage, reset: boolean): void {
    this.operationsRepayments = reset
      ? page.repayments
      : [...this.operationsRepayments, ...page.repayments]
    if (page.totalRecords !== undefined) this.operationsTotalRecords = page.totalRecords
    if (page.totalPaymentAmount !== undefined) {
      this.operationsTotalPaymentAmount = page.totalPaymentAmount
    }
    this.operationsHasMore = this.operationsSummaryLoaded
      ? this.operationsRepayments.length < this.operationsTotalRecords
      : page.repayments.length >= this.operationsPageSize
  }

  private async loadOperationsSummary(
    recordView: RecordRepaymentView,
    expectedLoadSequence: number,
  ): Promise<void> {
    try {
      const summary = await this.repaymentsService.fetchOperationsRepaymentSummary(
        OPERATIONS_RECORD_DATA_SOURCES[recordView],
        { signal: this.lifecycleAbort.signal },
      )
      if (
        this.destroyed
        || expectedLoadSequence !== this.loadSequence
        || this.activeRecordView !== recordView
      ) return
      this.operationsTotalRecords = summary.totalRecords
      this.operationsTotalPaymentAmount = summary.totalPaymentAmount
      this.operationsSummaryLoaded = true
      this.operationsHasMore = this.operationsRepayments.length < this.operationsTotalRecords
    } catch (error) {
      if (!this.destroyed && !this.isRequestAborted(error)) {
        // The record page is already usable; retain page-size-based scrolling if its total fails.
        this.operationsSummaryLoaded = false
      }
    }
  }

  private resetOperationsReport(): void {
    this.operationsRepayments = []
    this.operationsTotalRecords = 0
    this.operationsTotalPaymentAmount = 0
    this.operationsLoadingMore = false
    this.operationsHasMore = false
    this.operationsSummaryLoaded = false
  }

  private async searchMerchantOptions(
    term: string,
    searchSequence: number,
  ): Promise<void> {
    const normalizedTerm = term.trim()
    this.merchantOptionsLoading = true
    try {
      const merchants = await this.repaymentsService.searchMerchantsByName(
        normalizedTerm,
        { signal: this.lifecycleAbort.signal },
      )
      if (this.destroyed || searchSequence !== this.merchantSearchSequence) return
      this.merchantOptions = merchants
        .filter((merchant) => merchant?.id && merchant?.name)
        .map((merchant) => ({ label: String(merchant.name), value: String(merchant.id) }))
        .sort((left, right) => left.label.localeCompare(right.label))
    } catch {
      if (searchSequence === this.merchantSearchSequence) {
        this.merchantOptions = []
        this.addSelectedMerchantOption()
      }
    } finally {
      if (!this.destroyed && searchSequence === this.merchantSearchSequence) {
        this.merchantOptionsLoading = false
      }
    }
  }

  private addSelectedMerchantOption(): void {
    if (
      this.selectedMerchantId &&
      this.selectedMerchantLabel &&
      !this.merchantOptions.some((option) => option.value === this.selectedMerchantId)
    ) {
      this.merchantOptions = [
        { label: this.selectedMerchantLabel, value: this.selectedMerchantId },
        ...this.merchantOptions,
      ]
    }
  }

  private restoreAllSalesFilters(): void {
    try {
      const savedMerchant = JSON.parse(
        sessionStorage.getItem(ReportsComponent.allSalesMerchantStorageKey) ?? '[]',
      )
      if (Array.isArray(savedMerchant)) {
        this.selectedMerchantId = savedMerchant[0] ? String(savedMerchant[0]) : null
      } else if (savedMerchant && typeof savedMerchant === 'object') {
        this.selectedMerchantId = savedMerchant.id ? String(savedMerchant.id) : null
        this.selectedMerchantLabel = typeof savedMerchant.label === 'string'
          ? savedMerchant.label
          : ''
      } else {
        this.selectedMerchantId = typeof savedMerchant === 'string' && savedMerchant
          ? savedMerchant
          : null
      }
      this.terminalOnly = sessionStorage.getItem(
        ReportsComponent.allSalesTerminalOnlyStorageKey,
      ) === 'true'
    } catch {
      this.selectedMerchantId = null
      this.selectedMerchantLabel = ''
      this.terminalOnly = false
    }
  }

  private embeddedMerchantId(): string | null {
    const params = this.route.snapshot.queryParamMap
    const identifierType = params.get('identifierType')
    const identifierValue = params.get('identifierValue')?.trim()
    const merchantId = identifierType === 'merchant_id'
      ? identifierValue
      : params.get('merchant_id')?.trim()
    return merchantId || null
  }

  private persistAllSalesFilters(): void {
    if (this.isEmbeddedAllSales) return
    try {
      sessionStorage.setItem(
        ReportsComponent.allSalesMerchantStorageKey,
        JSON.stringify(this.selectedMerchantId
          ? { id: this.selectedMerchantId, label: this.selectedMerchantLabel }
          : null),
      )
      sessionStorage.setItem(
        ReportsComponent.allSalesTerminalOnlyStorageKey,
        String(this.terminalOnly),
      )
    } catch {
      // Session storage can be unavailable in privacy-restricted browsers.
    }
  }

  private async loadAllSalesReport(): Promise<void> {
    if (!this.selectedMerchantId) {
      this.allSalesLoading = false
      this.allSalesError = null
      this.allSalesMonths = []
      this.allSalesRepayments = []
      this.allSalesMerchantTables = []
      return
    }

    this.allSalesLoading = true
    this.allSalesError = null
    try {
      const repayments = await this.repaymentsService.fetchAllSalesRepayments(
        [this.selectedMerchantId],
        { signal: this.lifecycleAbort.signal },
      )
      if (this.destroyed) return
      this.allSalesRepayments = repayments
      const selectedMerchant = repayments.find(
        (repayment) => repayment.merchant_id === this.selectedMerchantId,
      )
      if (selectedMerchant) {
        this.selectedMerchantLabel = this.relatedName(
          selectedMerchant.merchants,
          this.selectedMerchantLabel,
        )
        this.addSelectedMerchantOption()
        this.persistAllSalesFilters()
      }
      this.buildAllSalesReport(repayments)
    } catch (error) {
      if (this.destroyed || this.lifecycleAbort.signal.aborted || this.isRequestAborted(error)) return
      this.allSalesError = error instanceof Error ? error.message : 'Failed to load merchant report.'
    } finally {
      if (!this.destroyed) this.allSalesLoading = false
    }
  }

  private buildAllSalesReport(repayments: AllSalesRepayment[]): void {
    const months = new Set<string>()
    const merchantGroups = new Map<string, AllSalesRepayment[]>()
    repayments.forEach((repayment) => {
      const month = this.monthKey(repayment.repayment_date)
      if (!month) return
      months.add(month)
      const merchantName = this.relatedName(repayment.merchants, 'Unassigned merchant')
      const merchantRepayments = merchantGroups.get(merchantName) ?? []
      merchantRepayments.push(repayment)
      merchantGroups.set(merchantName, merchantRepayments)
    })

    this.allSalesMonths = [...months].sort((left, right) => right.localeCompare(left))
    this.allSalesMerchantTables = [...merchantGroups.entries()]
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([merchantName, merchantRepayments]) => this.buildMerchantSalesTable(
        merchantName,
        merchantRepayments,
        merchantRepayments[0]?.merchant_id,
      ))
  }

  private buildMerchantSalesTable(
    merchantName: string,
    repayments: AllSalesRepayment[],
    merchantId?: string,
  ): AllSalesMerchantTable {
    const columns = new Map<string, AllSalesColumn>()
    const valuesByMonth = new Map<string, Record<string, SalesMetrics>>()
    repayments.forEach((repayment) => {
      const cashAdvanceName = this.relatedName(repayment.cash_advances, 'No cash advance')
      const terminalName = this.relatedName(repayment.terminals, 'No terminal')
      const columnCashAdvanceName = this.terminalOnly ? 'Terminals' : cashAdvanceName
      const key = `${columnCashAdvanceName}\u0000${terminalName}`
      columns.set(key, {
        key,
        cashAdvanceName: columnCashAdvanceName,
        terminalName,
        cashAdvanceId: repayment.cash_advance_id,
        terminalId: repayment.terminal_id,
      })

      const month = this.monthKey(repayment.repayment_date)
      if (!month) return
      const monthValues = valuesByMonth.get(month) ?? {}
      monthValues[key] = this.addSalesMetrics(monthValues[key] ?? this.emptySalesMetrics(), repayment)
      valuesByMonth.set(month, monthValues)
    })

    const sortedColumns = [...columns.values()].sort((left, right) =>
      this.naturalNameCollator.compare(left.cashAdvanceName, right.cashAdvanceName)
      || this.naturalNameCollator.compare(left.terminalName, right.terminalName),
    )
    const cashAdvances = new Map<string, AllSalesColumn[]>()
    sortedColumns.forEach((column) => {
      const terminals = cashAdvances.get(column.cashAdvanceName) ?? []
      terminals.push(column)
      cashAdvances.set(column.cashAdvanceName, terminals)
    })
    const rows = this.allSalesMonths.map((month) => {
      const values = valuesByMonth.get(month) ?? {}
      return { month, values, total: this.sumSalesMetrics(Object.values(values)) }
    })
    const columnTotals = Object.fromEntries(
      sortedColumns.map((column) => [
        column.key,
        this.sumSalesMetrics(rows.map((row) => row.values[column.key] ?? this.emptySalesMetrics())),
      ]),
    )
    return {
      merchantId,
      merchantName,
      cashAdvances: [...cashAdvances.entries()].map(([name, columns]) => ({ name, columns })),
      rows,
      columnTotals,
      grandTotal: this.sumSalesMetrics(rows.map((row) => row.total)),
    }
  }

  private monthKey(date: string | undefined): string | null {
    const isoMatch = date?.match(/^(\d{4})-(\d{2})/)
    if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}`

    const ukMatch = date?.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/)
    return ukMatch ? `${ukMatch[3]}-${ukMatch[2].padStart(2, '0')}` : null
  }

  private repaymentMonthKey(value: string | undefined): string | null {
    const match = value?.match(/^(\d{4})(\d{1,2})$/)
    if (!match) return null
    const month = Number(match[2])
    return month >= 1 && month <= 12 ? `${match[1]}-${match[2].padStart(2, '0')}` : null
  }

  private relatedName(value: AllSalesRepayment['merchants'], fallback: string): string {
    const relation = Array.isArray(value) ? value[0] : value
    return relation?.name?.trim() || fallback
  }

  private emptySalesMetrics(): SalesMetrics {
    return { amount: 0, cardTransactions: 0, count: 0 }
  }

  private addSalesMetrics(total: SalesMetrics, repayment: AllSalesRepayment): SalesMetrics {
    return {
      amount: total.amount + this.toNumber(repayment.payment_amount),
      cardTransactions: total.cardTransactions + this.toNumber(repayment.card_transactions),
      count: total.count + 1,
    }
  }

  private sumSalesMetrics(values: SalesMetrics[]): SalesMetrics {
    return values.reduce(
      (total, value) => ({
        amount: total.amount + value.amount,
        cardTransactions: total.cardTransactions + value.cardTransactions,
        count: total.count + value.count,
      }),
      this.emptySalesMetrics(),
    )
  }

  private buildRows(
    dates: string[],
    aggregates: RepaymentDailyAggregate[],
  ): ReportRow[] {
    const rowsByDate = new Map<string, ReportRow>(
      dates.map((date) => [
        date,
        { repaymentDate: date, values: {} },
      ]),
    )

    aggregates.forEach((aggregate) => {
      const repaymentDate = this.dateOnly(aggregate[this.dateField])
      const source = this.dailyRepaymentView === 'monthly' && this.monthlyAllSources
        ? aggregate.data_source?.trim() || null
        : this.canonicalDataSource(aggregate.data_source)
      if (!repaymentDate || !source || !rowsByDate.has(repaymentDate)) return

      const row = rowsByDate.get(repaymentDate)!
      const value = row.values[source] ?? { sum: 0, count: 0 }
      value.sum += this.toNumber(aggregate.repayment_sum)
      value.count += this.toNumber(aggregate.repayment_count)
      row.values[source] = value
    })

    return dates.slice().reverse().map((date) => rowsByDate.get(date)!)
  }

  private buildGroupedReportRows(
    aggregates: RepaymentDailyAggregate[],
  ): RepaymentReportTableGroup[] {
    const groups = new Map<string, Map<string, RepaymentReportTableRow>>()
    aggregates.forEach((aggregate) => {
      const repaymentDate = this.dateOnly(aggregate.repayment_date)
      const createdDate = this.dailyRepaymentView === 'monthly'
        ? this.repaymentMonthKey(aggregate.repayment_month)
        : this.dateOnly(aggregate.created_date)
      const source = this.dailyRepaymentView === 'monthly' && this.monthlyAllSources
        ? aggregate.data_source?.trim() || UNSPECIFIED_DATA_SOURCE
        : this.canonicalDataSource(aggregate.data_source)
      if (!createdDate || !source) return

      const groupKey = this.dailyRepaymentView === 'monthly'
        ? this.monthKey(createdDate)
        : repaymentDate
      if (!groupKey) return
      const rowKey = this.dailyRepaymentView === 'monthly' ? groupKey : createdDate
      const rows = groups.get(groupKey) ?? new Map<string, RepaymentReportTableRow>()
      const row = rows.get(rowKey) ?? { createdDate: rowKey, values: {} }
      const value = row.values[source] ?? { sum: 0, count: 0 }
      value.sum += this.toNumber(aggregate.repayment_sum)
      value.count += this.toNumber(aggregate.repayment_count)
      row.values[source] = value
      rows.set(rowKey, row)
      groups.set(groupKey, rows)
    })

    return [...groups.entries()]
      .sort(([left], [right]) => right.localeCompare(left))
      .map(([repaymentDate, rows]) => ({
        repaymentDate: this.dailyRepaymentView === 'this-month' ? repaymentDate : undefined,
        rows: [...rows.values()].sort((left, right) => right.createdDate.localeCompare(left.createdDate)),
      }))
  }

  private async loadCellRepayments(): Promise<void> {
    if (!this.selectedCell) return
    const selection = this.selectedCell
    const loadSequence = ++this.detailLoadSequence
    this.detailLoading = true
    this.detailError = null
    this.detailRepayments = []

    try {
      const repayments = await this.repaymentsService.fetchReportCellRepayments(
        this.dateField,
        selection.date,
        REPORT_DATA_SOURCE_VALUES[selection.source],
        { signal: this.lifecycleAbort.signal },
      )
      if (this.destroyed || loadSequence !== this.detailLoadSequence) return
      this.detailRepayments = this.sortRepaymentsByDateDescending(repayments)
    } catch (error) {
      if (this.destroyed || this.isRequestAborted(error) || loadSequence !== this.detailLoadSequence) return
      this.detailError =
        error instanceof Error ? error.message : 'Failed to load repayments.'
    } finally {
      if (!this.destroyed && loadSequence === this.detailLoadSequence) {
        this.detailLoading = false
      }
    }
  }

  onGroupedReportCellSelected(selection: RepaymentReportCellSelection): void {
    this.selectedCell = null
    this.selectedAllSalesCell = null
    this.selectedGroupedReportCell = selection
    void this.loadGroupedReportCellRepayments()
  }

  private async loadGroupedReportCellRepayments(): Promise<void> {
    if (!this.selectedGroupedReportCell) return
    const selection = this.selectedGroupedReportCell
    const loadSequence = ++this.detailLoadSequence
    this.detailLoading = true
    this.detailError = null
    this.detailRepayments = []
    try {
      const source = this.canonicalDataSource(selection.source)
      const includeUnspecified = selection.source === UNSPECIFIED_DATA_SOURCE
      const dataSources = source ? REPORT_DATA_SOURCE_VALUES[source] : [selection.source]
      const repayments = await this.repaymentsService.fetchGroupedReportCellRepayments(
        { ...selection, dataSources, includeUnspecified },
        { signal: this.lifecycleAbort.signal },
      )
      if (this.destroyed || loadSequence !== this.detailLoadSequence) return
      this.detailRepayments = this.sortRepaymentsByDateDescending(repayments)
    } catch (error) {
      if (this.destroyed || this.isRequestAborted(error) || loadSequence !== this.detailLoadSequence) return
      this.detailError = error instanceof Error ? error.message : 'Failed to load repayments.'
    } finally {
      if (!this.destroyed && loadSequence === this.detailLoadSequence) this.detailLoading = false
    }
  }

  get groupedReportCellLabel(): string {
    if (!this.selectedGroupedReportCell) return ''
    const date = this.selectedGroupedReportCell.createdDate ?? this.selectedGroupedReportCell.repaymentMonth ?? ''
    return `${date ? (date.length === 7 ? date : this.formatDate(date)) : ''} · ${this.selectedGroupedReportCell.source}`
  }

  onAllSalesCellSelected(selection: AllSalesCellSelection): void {
    this.selectedCell = null
    this.selectedAllSalesCell = selection
    void this.loadAllSalesCellRepayments()
  }

  private async loadAllSalesCellRepayments(): Promise<void> {
    if (!this.selectedAllSalesCell) return
    const selection = this.selectedAllSalesCell
    const loadSequence = ++this.detailLoadSequence
    this.detailLoading = true
    this.detailError = null
    this.detailRepayments = []

    try {
      const repaymentIds = this.matchingAllSalesRepaymentIds(selection)
      const repayments = await this.repaymentsService.fetchAllSalesCellRepayments({
        ...selection,
        repaymentIds,
      }, {
        signal: this.lifecycleAbort.signal,
      })
      if (this.destroyed || loadSequence !== this.detailLoadSequence) return
      this.detailRepayments = this.sortRepaymentsByDateDescending(repayments)
    } catch (error) {
      if (this.destroyed || this.isRequestAborted(error) || loadSequence !== this.detailLoadSequence) return
      this.detailError = error instanceof Error ? error.message : 'Failed to load repayments.'
    } finally {
      if (!this.destroyed && loadSequence === this.detailLoadSequence) this.detailLoading = false
    }
  }

  private isRequestAborted(error: unknown): boolean {
    if (!error) return false
    if (typeof error === 'object' && 'name' in error && (error as { name?: string }).name === 'AbortError') {
      return true
    }
    return error instanceof Error && /(?:signal\s+)?aborted/i.test(error.message)
  }

  private sortRepaymentsByDateDescending(repayments: Repayment[]): Repayment[] {
    return [...repayments].sort((left, right) => {
      const leftDate = this.repaymentSortTimestamp(left)
      const rightDate = this.repaymentSortTimestamp(right)
      return rightDate - leftDate
    })
  }

  private matchingAllSalesRepaymentIds(selection: AllSalesCellSelection): string[] {
    return this.allSalesRepayments
      .filter((repayment) =>
        repayment.merchant_id === selection.merchantId
        && this.monthKey(repayment.repayment_date) === selection.month
        && (!selection.filterByCashAdvance || repayment.cash_advance_id === selection.cashAdvanceId)
        && (!selection.filterByTerminal || repayment.terminal_id === selection.terminalId),
      )
      .map((repayment) => repayment.id)
      .filter((id): id is string => Boolean(id))
  }

  private repaymentSortTimestamp(repayment: Repayment): number {
    const date = repayment.repayment_date || repayment.created_date
    const timestamp = date ? new Date(date).getTime() : 0
    return Number.isFinite(timestamp) ? timestamp : 0
  }

  private reportDates(
    baseDates: string[],
    aggregates: RepaymentDailyAggregate[],
  ): string[] {
    const dates = new Set(baseDates)
    aggregates.forEach((aggregate) => {
      const date = this.dateOnly(aggregate[this.dateField])
      if (date) dates.add(date)
    })
    return [...dates].sort()
  }

  private toNumber(value: number | string | undefined): number {
    const numberValue = Number(value)
    return Number.isFinite(numberValue) ? numberValue : 0
  }

  private canonicalDataSource(
    value: string | undefined,
  ): (typeof REPORT_DATA_SOURCES)[number] | null {
    const source = value?.trim().toLowerCase()
    return source ? REPORT_DATA_SOURCE_NAMES[source] ?? null : null
  }

  private get dateField(): 'repayment_date' | 'created_date' {
    return this.dateMode === 'repayment' ? 'repayment_date' : 'created_date'
  }

  private reportDateRange(): string[] {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return Array.from({ length: this.daysBack }, (_, index) => {
      const date = new Date(today)
      date.setDate(today.getDate() - (this.daysBack - 1 - index))
      return this.toDateKey(date)
    })
  }

  private selectedCreatedMonthDate(): Date {
    const today = new Date()
    return new Date(today.getFullYear(), today.getMonth() + this.createdMonthOffset, 1)
  }

  private selectedMonthStart(): string {
    return this.toDateKey(this.selectedCreatedMonthDate())
  }

  private selectedMonthEnd(): string {
    const month = this.selectedCreatedMonthDate()
    return this.toDateKey(new Date(month.getFullYear(), month.getMonth() + 1, 1))
  }

  private dayAfterToday(): string {
    const today = new Date()
    return this.toDateKey(new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1))
  }

  private normalizeDaysBack(value: number | string | null): number {
    const parsed = Number(value)
    return Number.isInteger(parsed) && parsed >= 1 && parsed <= 365 ? parsed : 7
  }

  private csvValue(value: string): string {
    return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value
  }

  private dateOnly(value: string | undefined): string | null {
    const match = value?.match(/^\d{4}-\d{2}-\d{2}/)
    return match?.[0] ?? null
  }

  private toDateKey(date: Date): string {
    const pad = (value: number) => String(value).padStart(2, '0')
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
  }
}
